import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Col,
    Empty,
    Flex,
    Skeleton,
    Typography,
    Row,
} from '@app/ui';

import ErrorBoundary from '@app/ErrorBoundary';
import ResizableTable from '@app/components/common/ResizableTable';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import { getCollectionsTableColumns } from '@app/collections/hooks/getCollectionsTableColumns';

type CollectionsTableProps = {
    hasSelection: boolean;
    _archiveCollections: () => void;
    _openCreateModal: () => void;
    _items: object[];
    isLoading: boolean;
    columns: ReturnType<typeof getCollectionsTableColumns>;
};

export const CollectionsTable = ({
    hasSelection,
    _archiveCollections,
    _openCreateModal,
    _items,
    isLoading,
    columns,
}: CollectionsTableProps) => {
    const { t } = useTranslation(['tools', 'common']);
    return (
        <Card
            style={{
                marginTop: 24,
            }}
        >
            <Flex
                align="center"
                style={{
                    marginBottom: 24,
                }}
            >
                <Flex flex={1}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                        }}
                    >
                        {t('tools:collections.title')}
                    </Typography.Title>
                </Flex>
                <Flex>
                    <Button
                        type="default"
                        disabled={!hasSelection}
                        onClick={_archiveCollections}
                        data-test="archive-collections-btn"
                        icon={<i className="fa-regular fa-folder-open" />}
                        style={{
                            color: hasSelection
                                ? 'var(--tertiary)'
                                : 'rgba(0, 0, 0, 0.25)',
                            borderColor: hasSelection
                                ? 'var(--tertiary)'
                                : 'rgba(0, 0, 0, 0.25)',
                            backgroundColor: '#fff',
                        }}
                    >
                        {t('tools:collections.archive')}
                    </Button>
                    <div className="-m-l-2">
                        <Button
                            type="primary"
                            onClick={_openCreateModal}
                            data-test="create-collection-btn"
                            icon={<i className="fa-regular fa-images" />}
                        >
                            {t('tools:collections.create')}
                        </Button>
                    </div>
                </Flex>
            </Flex>
            <Row gutter={16}>
                <Col span={24}>
                    <ErrorBoundary FallbackComponent={CrashedComponent}>
                        {_items.length > 0 || isLoading ? (
                            <ResizableTable
                                columns={columns as any[]}
                                dataSource={isLoading ? [] : _items}
                                loading={isLoading}
                                rowKey="id"
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: false,
                                }}
                                locale={{
                                    emptyText: isLoading ? (
                                        <Skeleton
                                            active
                                            paragraph={{ rows: 10 }}
                                        />
                                    ) : (
                                        <Empty />
                                    ),
                                }}
                            />
                        ) : (
                            <div className="-text-center -m-t-4">
                                <h1 className="gds-text--header-sm -m-b-3">
                                    {t(
                                        'tools:collections.alerts.noCollections',
                                    )}
                                </h1>
                            </div>
                        )}
                    </ErrorBoundary>
                </Col>
            </Row>
        </Card>
    );
};
